import { Component, EventEmitter, Input, Output, ViewChild, forwardRef, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  MAT_LEGACY_SLIDE_TOGGLE_DEFAULT_OPTIONS,
  MatLegacySlideToggle,
  MatLegacySlideToggleChange,
  MatLegacySlideToggleDefaultOptions,
  MatLegacySlideToggleModule,
} from '@angular/material/legacy-slide-toggle';

@Component({
  selector: 'ptl-sg-toggle',
  standalone: true,
  imports: [MatLegacySlideToggleModule],
  templateUrl: './sg-toggle.component.html',
  styleUrls: ['./sg-toggle.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SgToggleComponent),
      multi: true,
    },
  ],
  viewProviders: [
    {
      // This template's toggle only, over any defaults from above. Read per change event: while controlled, the inner
      // toggle puts its native input back and only reports the click.
      provide: MAT_LEGACY_SLIDE_TOGGLE_DEFAULT_OPTIONS,
      useFactory: (): MatLegacySlideToggleDefaultOptions => {
        const host = inject(SgToggleComponent);
        return {
          ...inject(MAT_LEGACY_SLIDE_TOGGLE_DEFAULT_OPTIONS, { skipSelf: true }),
          get disableToggleValue() {
            return host.controlled;
          },
        };
      },
    },
  ],
})
export class SgToggleComponent implements ControlValueAccessor {
  @Input() disabled = false;
  @Input() checked = false;
  @Input() size: 'small' | 'medium' = 'medium';
  @Input() labelPosition: 'before' | 'after' = 'after';
  @Input() waitingList = false;
  @Input() ariaLabel: string;
  /**
   * Only asks for the change: emits the requested value but keeps showing `[checked]`, so a host that declines it has
   * nothing to put back. For `[checked]` + `(toggleChange)`; it doesn't report to `ngModel` / `formControl`. The
   * event's `source` still shows the current value.
   */
  @Input() controlled = false;

  @Output() toggleChange = new EventEmitter<MatLegacySlideToggleChange>();

  @ViewChild(MatLegacySlideToggle, { static: true }) private slideToggle: MatLegacySlideToggle;

  private onChange = (value: boolean) => {
    // ControlValueAccessor callback
  };
  private onTouched = () => {
    // ControlValueAccessor callback
  };

  // ControlValueAccessor implementation
  writeValue(value: boolean): void {
    this.checked = value || false;
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /** Every click, in both modes; the inner toggle only emits `change` when it flipped itself. */
  onRequestedToggle(): void {
    if (this.controlled) {
      this.toggleChange.emit(new MatLegacySlideToggleChange(this.slideToggle, !this.checked));
    }
  }

  onToggle(event: MatLegacySlideToggleChange): void {
    this.checked = event.checked;
    this.onChange(this.checked);
    this.toggleChange.emit(event);
  }

  onBlur(): void {
    this.onTouched();
  }
}
